/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import isObject from 'lodash/isObject';

import CommunicatorHeartbeat from './heartbeat/communicator-heartbeat';
import CommunicatorManifest from './manifest/communicator-manifest';
import CommunicatorQueue from './queue/communicator-queue';
import CommunicatorReport from './report/communicator-report';
import CommunicatorUpdate from './update/communicator-update';
import ErrorLevel from '../error/error-level';
import Store from '../store/store';
import config from '../../config';

// TODO #94 Přidat unit testy pro nové funkcionality spojené s offline módem.
/**
 * Komunikátor pro Ki-Wi Server
 * @implements {EventBus}
 */
class Communicator {
  /**
   * Vytvoří komunikátor zařízení se serverem.
   * @param {ContentCommunicator} contentCommunicator
   */
  constructor(contentCommunicator) {
    // Komunikátory
    this.CommunicatorManifest = CommunicatorManifest;
    this.communicatorHeartbeat = new CommunicatorHeartbeat();
    this.communicatorQueue = new CommunicatorQueue();
    this.communicatorReport = CommunicatorReport;
    this.communicatorUpdate = new CommunicatorUpdate();
    // Příznak, jestli je synchronizace zapnuta.
    this.connected = false;
    this.contentCommunicator = contentCommunicator;
    this.emitter = new EventEmitter();
    this.latestTimestamp = null;
    // Výchozí doba čekání před novým pokusem o synchronizaci.
    this.syncDelayBase = config.device.communicator.syncDelayBase;
    // Identifikátor intervalu ve kterém probíhá synchronizace.
    this.syncInterval = null;
    // Navázání naslouchání na instanci.
    this.onError = this.onError.bind(this);
  }

  /**
   * Spustí kontinuální synchronizaci obsahu zařízení.
   *
   * Vracet objekty ve store, které byly během operace načteny.
   *
   * Vracet plain objekty. Player aplikace sama rozhoduje, jaké datový sklad bude používat.
   * Zároveň by neměla mít přístup k interním datům device.
   *
   * Vracet mapu polí modelů. Klíčem v mapě je název datového zdroje.
   * Např. `result.medium[0]` bude mít obsah média prvního načteného média.
   * Možná ještě zvážit mapu map modelů, kde by druhým klíčem bylo id modelu.
   * Např. `result.medium.foo` bude mít obsah média s identifikátorem `foo`.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {string} [contentTimestamp] Časový otisk posledního úspěšně synchronizovaného obsahu.
   * @returns {Promise.<undefined>} Vyřeší se pod dokončení první synchronizace.
   */
  connect(serverUrl, deviceId, contentTimestamp) {
    return new Promise((resolve) => {
      if (this.connected) {
        throw new Error('Already connected.');
      }
      // Uzamče další spuštění synchronizace.
      this.connected = true;
      // Nastavení posledního úspěšně synchronizovaného obsahu.
      this.latestTimestamp = contentTimestamp;
      // Naplánuje interval, ve kterém se bude spuštět synchronizace.
      this.syncInterval = setInterval(
        () => this.onSyncInterval(serverUrl, deviceId),
        this.syncDelayBase
      );
      // Spustí první synchronizaci ihned.
      return resolve(
        this.fetchHeartbeat(serverUrl, deviceId).catch(this.onError)
      );
    });
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   * @returns {undefined} Nic.
   */
  destroy() {
    // Zruší interval synchronizace.
    clearInterval(this.syncInterval);
    this.syncInterval = null;
  }

  /**
   * Doručí jeden příkaz z fronty příkazů zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {Array.<string>} [queueIdArray] Seznam identifikátorů front příkazů.
   * @returns {Promise.<CommandEnum|undefined>} Doručený příkaz, pokud je nějaký dostupný.
   */
  fetchCommand(serverUrl, deviceId, queueIdArray) {
    return new Promise((resolve) => {
      if (isNil(queueIdArray) || queueIdArray.length === 0) {
        // Bez fronty příkazů není nic dostupné.
        return resolve();
      }
      // Vybere se první dostupná fronta.
      const queueId = queueIdArray[0];
      return resolve(
        this.communicatorQueue.fetch(queueId, deviceId, serverUrl)
      );
    });
  }

  /**
   * Doručí obsah zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {string} heartbeatTimestamp Časový otisk okamžiku synchronizace.
   * @returns {Promise.<ContentInfo|undefined>} Doručený obsah, pokud je nějaký dostupný.
   */
  fetchContent(serverUrl, deviceId, heartbeatTimestamp) {
    return new Promise((resolve) => {
      // Kontrola, že je heartbeat timestamp novější, než aktuálně přehrávaný obsah.
      if (!Communicator.isBefore(heartbeatTimestamp, this.latestTimestamp)) {
        return resolve();
      }
      // Vytvoření nového datového skladu, do kterého se bude vkládat nový obsah.
      // TODO #84 Datový sklad s novým obsahem:
      // - Postupně nahradit implementací datového skladu i do komunikátoru.
      // - Vytvářet pro každý obsah nový datový sklad? Nebo recyklovat jeden lokální?
      // - Do budoucna vyčlenit inicializaci do co nejvyšší úrovně.
      const store = new Store();
      return resolve(
        // Stáhne obsah.
        this.contentCommunicator
          .fetch(store, heartbeatTimestamp, deviceId, serverUrl)
          .then((contentInfo) => {
            if (isObject(contentInfo)) {
              const receivedTimestamp = contentInfo.session.contentTimestamp;
              // TODO #84 Prověřit logiku přepínání obsahu https://gitlab.com/ki-wi/player-light/player-lighter/issues/67
              // Kontrola, že je timestamp načteného obsahu novější, než aktuálně přehrávaný obsah.
              if (
                Communicator.isBefore(receivedTimestamp, this.latestTimestamp)
              ) {
                this.latestTimestamp = receivedTimestamp;
                // Do výsledku se přidá datový sklad, aby s ním mohl příjemce operovat.
                // TODO #84 Do budoucna vyčlenit inicializaci skladu do co vyšší úrovně a nebude třeba předávat.
                return contentInfo;
              }
            }
          })
      );
    });
  }

  /**
   * Odešle heartbeat a získá heartbeat data pro zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po úspěšném získání heartbeat dat a odeslání události heartbeatFetched s hodnotou typu HeartbeatFetched.
   */
  fetchHeartbeat(serverUrl, deviceId) {
    return (
      // Stáhne push data.
      this.communicatorHeartbeat
        .fetch(deviceId, serverUrl)
        // TODO #44 Zpracování reakce na heartbeat a posloupnost content / queue / version.
        // TODO #44 Co když heartbeat přijde dříve, než jsou předchozí informace zpracovány?
        .then((heartbeatFetched) => {
          // heartbeatFetched může být null v případě, že již probíhá fetch a přijde současně jiný požadavek.
          // TODO Správně by se mělo explicitně tento stav testovat pomocí isNil a nikoliv nepřímo pomocí !isObject.
          // TODO Tyto stavy by se neměly tiše spolknout, ale zalogovat jako warning nebo zaevidovat jako metrika.
          if (!isObject(heartbeatFetched)) {
            // Pokud se nepodařilo heartbeat načíst, tak se nic nedělá.
            return;
          }
          // Pokud se podařilo heartbeat načíst, tak se vyvolá událost, která o něm informuje.
          this.emitter.emit('heartbeatFetched', heartbeatFetched);
        })
    );
  }

  /**
   * Porovná časové otisky, jestli se obsah změnil a je nutné ho stahovat.
   * @param {string} [newTimestamp] Nový časový otisk.
   * @param {string} [oldTimestamp] Starý časový otisk.
   * @private
   * @returns {boolean} Příznak, jestli je datum novější.
   */
  static isBefore(newTimestamp, oldTimestamp) {
    let result;
    const newDate = new Date(newTimestamp);
    if (isNaN(newDate) || isNull(newTimestamp)) {
      // Pokud není dostupný nový timestamp, nestáhne se obsah.
      result = false;
    } else {
      const oldDate = new Date(oldTimestamp);
      if (isNaN(oldDate) || isNull(oldTimestamp)) {
        // Pokud není dostupný starý timestamp, stáhne se obsah.
        result = true;
      } else {
        // Pokud je nový timestamp novější, stáhne se obsah.
        result = newDate > oldDate;
      }
    }
    return result;
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  /** @inheritdoc */
  off(type, callback) {
    this.emitter.off(type, callback);
  }

  /** @inheritdoc */
  on(type, callback) {
    this.emitter.on(type, callback);
  }

  /**
   * Reakce uplynutí intervalu ve kterém se synchronizuje obsah.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @private
   * @returns {undefined} Nic.
   */
  onSyncInterval(serverUrl, deviceId) {
    // Spustí jednorázovou synchronizaci.
    this.fetchHeartbeat(serverUrl, deviceId).catch(this.onError);
  }

  /**
   * Odešle informace o zařízení na server.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @returns {Promise.<undefined>} Vyřeší se po úspěšném dokončení.
   */
  report(serverUrl, deviceId, reportInfo) {
    return this.communicatorReport.fetch(reportInfo, deviceId, serverUrl);
  }

  /**
   * Doručí všechny potřebné informace pro aktualizaci zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {FormatEnum} formatEnum Formát, ve kterém provést aktualizaci.
   * @param {UpdatePlatform} updatePlatform Platforma, ve které provést aktualizaci.
   * @returns {Promise.<Object>} Informace pro aktualizaci zařízení.
   */
  update(serverUrl, deviceId, formatEnum, updatePlatform) {
    return new Promise((resolve) => {
      // Kontrola vstupů.
      if (!formatEnum) {
        throw new TypeError('Invalid formatEnum.');
      }
      if (!updatePlatform) {
        throw new TypeError('Invalid updatePlatform.');
      }
      return resolve(
        // Zjistí se informace o verzi a dostupných manifestech.
        this.communicatorUpdate
          .fetch(deviceId, serverUrl)
          // TODO #44 Na co v tomto API položka version? Ta už byla kontrolována v heartbeat.
          .then(({ urlArray, version }) => {
            // Pokud není definována verze, nic neaktualizujeme.
            if (!version) {
              return Promise.resolve();
            }
            // Kontrola dostupných manifestů.
            if (!urlArray.length) {
              throw new Error('No version manifest available.');
            }
            // Stažení manifestů.
            return (
              // Jednotlivé manifesty se začnou sériově načítat, dokud nějaký nenalezne vyhovující build.
              urlArray
                .reduce(
                  (acc, url) =>
                    acc.catch(() => {
                      const communicatorManifest =
                        new this.CommunicatorManifest(url);
                      return (
                        // Zjistí se informace z manifestu.
                        communicatorManifest
                          .fetch()
                          .then((manifestData) => {
                            // Profiltruje se seznam dostupných buildů, aby odpovídal požadavkům ve vstupu.
                            const buildArray = manifestData.buildArray.filter(
                              (build) =>
                                build.formatEnum === formatEnum &&
                                build.updatePlatform === updatePlatform
                            );
                            // Pokud se build nenalezne, tak se vyvolá jednotné chybové hlášení.
                            // Chybě se sníží priorita, není pro logování důležitá.
                            if (!buildArray.length) {
                              const error = new Error(
                                'No matching build found.'
                              );
                              error.level = ErrorLevel.Warning;
                              throw error;
                            }
                            // Vrátí se informace o prvním vyhovujícím buildu.
                            // TODO #44 Co kdyby bylo odfilrovaných buildů více? Throw?
                            const [build] = buildArray;
                            return Promise.resolve({
                              buildId: build.buildId,
                              url: build.url,
                              version: manifestData.version
                            });
                          })
                          .catch((error) => {
                            this.onError(error);
                            throw error;
                          })
                      );
                    }),
                  // Pouze pro inicializaci série, nedostane se mimo metodu.
                  // eslint-disable-next-line prefer-promise-reject-errors
                  Promise.reject()
                )
                // Pokud neuspěje žádný, vyvolá se jednotné chybové hlášení.
                // Chybě se sníží priorita, není pro logování důležitá.
                .catch(() => {
                  const error = new Error(
                    'No version manifest satisfied requirements.'
                  );
                  error.level = ErrorLevel.Warning;
                  throw error;
                })
            );
          })
      );
    });
  }
}

/**
 * Byla získána push data ze serveru.
 * @event Communicator#heartbeatFetched
 * @param {HeartbeatFetched} heartbeatFetched Heartbeat data.
 */

export default Communicator;
